import { store } from '../../src/store/store'
import { CONSTANT } from './constant'
import ConfigurationService from '../components/configuration/configuration.service'

const configurationService = new ConfigurationService()
const consumer = {

  /**
   * Get Billing Address
   * @param decryptedData
   * @returns {{zip: string, city: string, countryCode: string, state: string, line2: string, line1: (*|string)}}
   */
  getBillingAddress (decryptedData = {}) {
    let billingAddress = decryptedData.billingAddress || (decryptedData.card ? decryptedData.card.billingAddress : {})
    return {
      line1: billingAddress ? billingAddress.line1 : '',
      line2: billingAddress ? billingAddress.line2 : '',
      city: billingAddress ? billingAddress.city : '',
      state: billingAddress ? billingAddress.state : '',
      countryCode: billingAddress ? billingAddress.countryCode : '',
      zip: billingAddress ? billingAddress.zip : '',
      name: billingAddress ? billingAddress.name : ''
    }
  },
  /**
   * Get shipping address
   * @param decryptedData
   * @returns {{zip: *, city: string, countryCode: *, state: *, line2: (*|string), line1: *}}
   */
  getShippingAddress (decryptedData = {}) {
    let shippingAddress = decryptedData.shippingAddress || (decryptedData.card ? decryptedData.card.billingAddress : {})
    return {
      line1: shippingAddress ? shippingAddress.line1 : '',
      line2: shippingAddress ? shippingAddress.line2 : '',
      city: shippingAddress ? shippingAddress.city : '',
      state: shippingAddress ? shippingAddress.state : '',
      countryCode: shippingAddress ? shippingAddress.countryCode : '',
      zip: shippingAddress ? shippingAddress.zip : '',
      name: shippingAddress ? shippingAddress.name : ''
    }
  },

  /**
   * Get Card Data
   * @param decryptedData
   * @returns {{tokenLastFour: string, panBin: string, dcf: (*|{name: string, logoUri: string, type: string, uri: string}), dateOfCardLastUsed: string, countryCode: string, panLastFour: *, digitalCardData: (*|{presentationName: string, artWidth: number, artUri: string, descriptorName: string, artHeight: number, status: string}), panExpirationYear: *, srcDigitalCardId: string, tokenBin: string, billingAddress: (*|{zip: string, city: string, countryCode: string, state: string, line2: string, line1: (*|string)}), dateOfCardCreated: string, digitalCardStatus: string, panExpirationMonth: *}}
   */
  getMaskedCard (decryptedData = {}) {
    const decryptedCard = decryptedData.card || {}
    return {
      panLastFour: decryptedCard.primaryAccountNumber || '',
      digitalCardData: this.getDigitalCard(decryptedData.artUri),
      panExpirationMonth: decryptedCard.panExpirationMonth || '',
      panExpirationYear: decryptedCard.panExpirationYear || '',
      billingAddress: this.getBillingAddress(decryptedData),
      dcf: this.getDcfData(),
      shippingAddress: this.getShippingAddress(decryptedData)
    }
  },

  /**
   * Get Digital Card Details
   * @returns {{presentationName: string, artWidth: number, artUri: string, descriptorName: string, artHeight: number, status: string}}
   */
  getDigitalCard (artUri) {
    const srcNetwork = store.getters.srcNetwork
    return {
      status: 'ACTIVE',
      presentationName: ` MyBank `,
      descriptorName: srcNetwork,
      artUri: this.getArtUri(srcNetwork, artUri),
      artHeight: 1,
      artWidth: 8
    }
  },

  /**
   * Get Phone Number
   * @param decryptedData
   * @returns {{phoneNumber: (*|string), countryCode: *}}
   */
  getPhoneNumber (decryptedData = {}) {
    return {
      countryCode: (decryptedData.consumerMobileNumber || {}).countryCode || '',
      phoneNumber: (decryptedData.consumerMobileNumber || {}).phoneNumber || ''
    }
  },

  /**
   * Get Consumer Data
   * @param decryptedData
   * @returns {{srcConsumerId: string, maskedConsumerMobileNumber: (*|{phoneNumber: (*|string), countryCode: *}), countryCode: string, maskedEmailAddress: *, maskedFirstName: *, maskedLastName: *}}
   */
  getConsumer (decryptedData = {}) {
    return {
      maskedEmailAddress: decryptedData.consumerEmailAddress || '',
      maskedConsumerMobileNumber: this.getPhoneNumber(decryptedData),
      maskedFirstName: decryptedData.consumerFirstName || '',
      maskedLastName: decryptedData.consumerLastName || ''
    }
  },

  /* eslint complexity: ["error", 10] */
  /**
   * Get Payload Response
   * @param decryptedData
   * @returns {{maskedConsumer: (*|{srcConsumerId: string, maskedConsumerMobileNumber: (*|{phoneNumber: (*|string), countryCode: *}), countryCode: string, maskedEmailAddress: *, maskedFirstName: *, maskedLastName: *}), encryptedPayload: string, shippingCountryCode: *, maskedCard: (*|{tokenLastFour: string, panBin: string, dcf: (*|{name: string, logoUri: string, type: string, uri: string}), dateOfCardLastUsed: string, countryCode: string, panLastFour: *, digitalCardData: (*|{presentationName: string, artWidth: number, artUri: string, descriptorName: string, artHeight: number, status: string}), panExpirationYear: *, srcDigitalCardId: string, tokenBin: string, billingAddress: (*|{zip: string, city: string, countryCode: string, state: string, line2: string, line1: (*|string)}), dateOfCardCreated: string, digitalCardStatus: string, panExpirationMonth: *}), shippingAddressZip: *, srcDigitalCardId: string, shippingAddressId: string, srcCorrelationId: string}}
   */
  getPayloadResponse (decryptedData = {}) {
    let payloadData = {
      maskedCard: this.getMaskedCard(decryptedData),
      maskedConsumer: this.getConsumer(decryptedData),
      shippingAddressZip: (decryptedData.shippingAddress || {}).zip || '',
      shippingCountryCode: (decryptedData.shippingAddress || {}).countryCode || '',
      maskedToken: this.getToken(decryptedData)
    }

    if (decryptedData.token) {
      payloadData.token = this.getToken(decryptedData)
    }

    if (decryptedData.dynamicData) {
      payloadData.dynamicData = decryptedData.dynamicData
    }

    if (decryptedData.srcTokenResultsData) {
      payloadData.srcTokenResultsData = decryptedData.srcTokenResultsData
    }

    return payloadData
  },

  /**
   * Get DCF Data
   * @returns {{name: string, logoUri: string, type: string, uri: string}}
   */
  getDcfData () {
    return {
      type: 'Browser',
      uri: 'mastercard_logo.png',
      logoUri: 'dcfLogoUrl',
      name: 'Mastercard'
    }
  },

  /**
   * Get Art Uri
   * @param srcNetwork
   * @returns {string}
   */
  getArtUri (srcNetwork, artUri) {
    let logo = CONSTANT.DEFAULT_CARD_LOGO[srcNetwork]
    return (artUri || (require('@/assets/images/cards/' + logo)))
  },

  /**
   * get  token
   * @param decryptedData
   * @returns {{paymentToken: (*|string), paymentAccountReference: (*|string), tokenExpirationMonth: (*|string), tokenExpirationYear: (*|string), tokenSecurityCode: (*|string)}}
   */
  getToken (decryptedData = {}) {
    let decryptedDataToken = decryptedData.token || {}
    return {
      paymentToken: decryptedDataToken.paymentToken,
      tokenExpirationMonth: decryptedDataToken.tokenExpirationMonth,
      tokenExpirationYear: decryptedDataToken.tokenExpirationYear,
      tokenSecurityCode: decryptedDataToken.tokenSecurityCode,
      paymentAccountReference: decryptedDataToken.paymentAccountReference,
      digitalCardData: this.getDigitalCard(decryptedData.artUri)
    }
  },

  /**
   * Get Decrypted Data
   * @returns {string}
   */
  getDecryptedData () {
    return configurationService.getConfigData().decryptedPayload
  },

  /* eslint complexity: ["error", 13] */
  /**
   * This will return the consumer login data
   * @param consumerData
   * @returns {any}
   */
  getConsumerLoginDetails (consumerData) {
    let dsaTransactionOptions, mobileNumber, consumerInfo
    if (Object.keys(consumerData).length) {
      dsaTransactionOptions = store.getters.dsaTransactionOptions
      mobileNumber = consumerData.countryCode && consumerData.countryCode.trim().split(' ')
      consumerInfo = {
        ...(dsaTransactionOptions.consumerEmailAddressRequested) && { emailAddress: consumerData.email || '' },
        ...(dsaTransactionOptions.consumerPhoneNumberRequested) && { mobileNumber: {
          countryCode: (mobileNumber && (mobileNumber[1].replace('+', '') || '')),
          phoneNumber: (consumerData.phoneNumber || '')
        } },
        ...(dsaTransactionOptions.consumerNameRequested) && {
          firstName: consumerData.firstName || '',
          lastName: consumerData.lastName || ''
        },
        nationalIdentifier: consumerData.nationalIdentifier || ''
      }
      return consumerInfo
    }
  }
}

export default consumer
